import { ContentObserver } from '@angular/cdk/observers';
import { Injectable, NgZone, Renderer2 } from '@angular/core';
import { Router } from '@angular/router';
import { MerchandiseService } from '@epicuro-next/data/merchandise';
import {
  selectAccountId,
  selectSiteId,
} from '@epicuro-next/state/session-state';
import { isNonNullish, isNullish } from '@epicuro-next/utilities';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { EMPTY, forkJoin, from, fromEvent, merge, Observable, of } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  distinctUntilKeyChanged,
  filter,
  map,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import {
  BUILDER_CONTENT_LOADED,
  BUILDER_ERROR_CASS,
  BUILDER_LOADING_CLASS,
  ERROR_DEBOUNCE_TIME,
  OBSERVE_DEBOUNCE_TIME,
} from '../constants';
import { BuilderContentService } from '../services/builder-content.service';

export interface FetchTemplateParams {
  site?: string;
  templateId?: string;
}

export interface RenderTemplateParams {
  builderApiKey: string;
  builderEntryId: string;
}

export interface BuilderContentState {
  builderApiKey?: string;
  builderEntryId?: string;
  builderHasError: boolean;
  builderLoading: boolean;
  targetRef?: HTMLElement;
}

export const initialState = {
  builderLoading: true,
  builderHasError: false,
};

@Injectable()
export class BuilderContentStore extends ComponentStore<BuilderContentState> {
  constructor(
    private builderContentService: BuilderContentService,
    private renderer: Renderer2,
    private merchandiseApi: MerchandiseService,
    private store: Store,
    private contentObserver: ContentObserver,
    private router: Router,
    private ngZone: NgZone,
  ) {
    super(initialState);
    this.checkErrorWhenContentChanged();
    this.navigateOnAnchorClick();
    this.builderContentService.appendScript();
  }

  private accountId$ = this.store.pipe(
    select(selectAccountId),
    filter(isNonNullish),
    distinctUntilChanged(),
  );

  private siteId$ = this.store.pipe(
    select(selectSiteId),
    filter(isNonNullish),
    distinctUntilChanged(),
  );

  public readonly builderLoading$ = this.select(
    ({ builderLoading }) => builderLoading,
  );

  public readonly builderHasError$ = this.select(
    ({ builderHasError }) => builderHasError,
  );

  public readonly targetRef$ = this.select(({ targetRef }) => targetRef).pipe(
    filter(isNonNullish),
    distinctUntilChanged(),
  );

  public readonly builderContentParams$ = this.select(
    ({ builderApiKey, builderEntryId }) => ({
      builderApiKey,
      builderEntryId,
    }),
    {
      debounce: true,
    },
  ).pipe(
    distinctUntilKeyChanged('builderEntryId'),
    filter<RenderTemplateParams>(
      ({ builderApiKey, builderEntryId }) =>
        isNonNullish(builderApiKey) && isNonNullish(builderEntryId),
    ),
  );

  private contentChanged$ = this.targetRef$.pipe(
    filter(isNonNullish),
    switchMap((targetRef) =>
      this.contentObserver
        .observe(targetRef)
        .pipe(debounceTime(OBSERVE_DEBOUNCE_TIME), take(1)),
    ),
  );

  public destroyLandingPage = this.effect<void>((void$) =>
    void$.pipe(
      switchMap(() => this.targetRef$),
      switchMap((targetRef) =>
        of(targetRef.querySelector(BUILDER_CONTENT_LOADED)).pipe(
          filter(isNonNullish),
          tap((element) => this.renderer.removeChild(targetRef, element)),
        ),
      ),
    ),
  );

  public readonly fetchTemplate = this.effect<FetchTemplateParams>((params$) =>
    params$.pipe(
      withLatestFrom(this.accountId$, this.siteId$),
      switchMap(([params, accountId, siteId]) =>
        forkJoin([
          this.merchandiseApi.getBuilderContentApiKey(
            params.site ?? siteId,
            accountId,
          ),
          this.getTemplateIdStream(
            params.site ?? siteId,
            accountId,
            params.templateId,
          ),
        ]).pipe(
          tapResponse(
            ([builderApiKey, builderEntryId]) =>
              this.patchState({
                builderEntryId,
                builderApiKey,
              }),
            () =>
              this.patchState({
                builderHasError: true,
              }),
          ),
        ),
      ),
    ),
  );

  public readonly renderTemplate = this.effect(
    (builderContentParams$: Observable<RenderTemplateParams>) =>
      builderContentParams$.pipe(
        withLatestFrom(this.targetRef$, (params, targetRef) => ({
          ...params,
          targetRef,
        })),
        tap<RenderTemplateParams & { targetRef: HTMLElement }>(
          ({ builderApiKey, builderEntryId, targetRef }) => {
            const builderContent = this.builderContentService.getBuilderElement(
              builderApiKey,
              builderEntryId,
            );

            this.renderer.appendChild(targetRef, builderContent);
          },
        ),
      ),
  )(this.builderContentParams$);

  public checkErrorWhenContentChanged = this.effect<void>((void$) =>
    void$.pipe(
      switchMap(() => this.contentChanged$),
      debounceTime(ERROR_DEBOUNCE_TIME),
      withLatestFrom(this.targetRef$, (_, targetRef) => targetRef),
      map((targetRef) =>
        [
          targetRef.querySelector(BUILDER_ERROR_CASS),
          targetRef.querySelector(BUILDER_LOADING_CLASS),
        ].filter(isNonNullish),
      ),
      filter((elements) => elements.length > 0),
      tap(() =>
        this.patchState({ builderHasError: true, builderLoading: false }),
      ),
    ),
  );

  public navigateOnAnchorClick = this.effect<void>((void$) =>
    void$.pipe(
      switchMap(() => this.contentChanged$),
      debounceTime(OBSERVE_DEBOUNCE_TIME),
      withLatestFrom(this.targetRef$, (_, targetRef) => targetRef),
      map((targetRef: HTMLElement) =>
        targetRef.querySelectorAll<HTMLAnchorElement>('a'),
      ),
      filter((anchors) => anchors.length > 0),
      map((anchors: NodeListOf<HTMLAnchorElement>) => Array.from(anchors)),
      tap((anchors: HTMLAnchorElement[]) => {
        anchors.forEach((anchor) => this.handleAnchorTransformation(anchor));
      }),
      switchMap((anchors: HTMLAnchorElement[]) => {
        const obs = anchors.map((anchor) => this.handleAnchorClick(anchor));

        this.patchState({
          builderLoading: false,
        });

        return merge(...obs);
      }),
    ),
  );

  private handleAnchorClick(elm: HTMLAnchorElement) {
    return fromEvent(elm, 'click').pipe(
      filter(() => isNullish(elm.getAttribute('target'))),
      tap((evt: MouseEvent) => evt.preventDefault()),
      map(() => elm.getAttribute('href')),
      switchMap((route) => {
        if (!route) return EMPTY;

        if (route.indexOf('next/') > -1) {
          route = route.split('next')[1] ?? '/';
        }

        return from(
          this.ngZone.run(() => this.router.navigateByUrl(`${route}`)),
        );
      }),
    );
  }

  public setTargetRef(targetRef: HTMLElement) {
    this.patchState({
      targetRef,
    });
  }

  private getTemplateIdStream(
    siteId: string,
    accountId: string,
    templateId?: string,
  ) {
    if (isNonNullish(templateId)) {
      return of(templateId);
    }

    return this.merchandiseApi.getBuilderContentTemplateId(siteId, accountId);
  }

  private handleAnchorTransformation(element: HTMLAnchorElement) {
    for (let i = 0; i < element.children.length; i++) {
      this.renderer.setStyle(
        element.children.item(i),
        'pointer-events',
        'none',
      );
    }
  }
}
